import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Collapse, Fade, useMediaQuery } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { config } from '../../Configs';
import { RecaptchaAction } from '../../Enums/recaptcha';
import { useTurnstileWidget } from '../../Hooks/useTurnstileWidget';
import TurnstileSuccessMock from './TurnstileSuccessMock';
import SweetAlert from '../Alerts/SweetAlert';
import { ALERT_TYPE } from '../Alerts/alert_enums';

export const TURNSTILE_SIZE = {
  NORMAL: 'normal',
  FLEXIBLE: 'flexible',
  COMPACT: 'compact'
};

// Turnstile managed/non-interactive documented dimensions.
export const TURNSTILE_WIDGET_DIMENSIONS_BY_SIZE = {
  [TURNSTILE_SIZE.COMPACT]: { width: 150, height: 140 },
  [TURNSTILE_SIZE.NORMAL]: { width: 300, height: 65 },
  // "flexible" adapts width, but 300x65 is the baseline widget size.
  [TURNSTILE_SIZE.FLEXIBLE]: { width: 300, height: 65 }
};

export const CAPTCHA_STATUS = {
  INTERACTING: 'interacting',
  RETRYING: 'retrying',
  RESOLVED: 'resolved',
  EXPIRED: 'expired',
  ERROR: 'error'
};

const STATUS_CONFIG = {
  [CAPTCHA_STATUS.INTERACTING]: {
    type: ALERT_TYPE.INFO,
    message: 'Completa la verificación para continuar'
  },
  [CAPTCHA_STATUS.RETRYING]: {
    type: ALERT_TYPE.WARNING,
    message: 'Estamos verificando, por favor espera...'
  },
  [CAPTCHA_STATUS.EXPIRED]: {
    type: ALERT_TYPE.WARNING,
    message: 'La verificación expiró'
  },
  [CAPTCHA_STATUS.ERROR]: {
    type: ALERT_TYPE.ERROR,
    message: 'No fue posible completar la verificación'
  }
};

const TurnstileManagedCaptcha = props => {
  const {
    action,
    locale,
    onExpired,
    onError,
    onLoaded,
    onResolved,
    tabindex,
    captchaRef,
    referenceEl,
    heightScale,
    theme: themeMode,
    size
  } = props;

  const isSmallScreen = useMediaQuery(theme =>
    theme.breakpoints.down(theme.breakpoints.values.sm)
  );

  // Switch to flexible layout on small screens to fill available width.
  let effectiveSize = size;
  if (isSmallScreen && size === TURNSTILE_SIZE.NORMAL) {
    effectiveSize = TURNSTILE_SIZE.FLEXIBLE;
  }

  const { width: baseWidth, height: baseHeight } =
    TURNSTILE_WIDGET_DIMENSIONS_BY_SIZE[effectiveSize] ||
    TURNSTILE_WIDGET_DIMENSIONS_BY_SIZE[TURNSTILE_SIZE.NORMAL];

  const classes = useStyles();
  const [captchaStatus, setCaptchaStatus] = useState(null);
  // Trigger state: incremented on window resize so the render re-reads dimensions.
  const [, setResizeTick] = useState(0);

  // Tracks consecutive errors: first triggers a silent retry, second propagates.
  const errorCountRef = useRef(0);
  // The component owns this ref to reset the widget on error/expire.
  // The consumer's captchaRef receives the same API after the widget loads.
  const ownCaptchaRef = useRef(null);
  // Both refs hold the last non-null value so exit animations play when status
  // returns to null: fadeKeyRef keeps the Fade key stable, lastAlertConfigRef
  // keeps SweetAlert mounted through the fade-out.
  const fadeKeyRef = useRef(null);
  const lastAlertConfigRef = useRef(null);

  const handleBeforeInteractive = useCallback(() => {
    // Cloudflare switched to interactive — reset so failures in this new cycle
    // are counted independently from any prior silent-verification errors.
    errorCountRef.current = 0;
    setCaptchaStatus(CAPTCHA_STATUS.INTERACTING);
  }, []);

  const handleResolved = useCallback(
    token => {
      errorCountRef.current = 0;
      setCaptchaStatus(null);
      onResolved(token);
    },
    [onResolved]
  );

  const handleExpired = useCallback(() => {
    errorCountRef.current = 0;
    setCaptchaStatus(CAPTCHA_STATUS.EXPIRED);
    const api = ownCaptchaRef.current;
    if (api && typeof api.reset === 'function') {
      api.reset();
    }
    onExpired();
  }, [onExpired]);

  const handleError = useCallback(() => {
    if (errorCountRef.current < 1) {
      errorCountRef.current = errorCountRef.current + 1;
      setCaptchaStatus(CAPTCHA_STATUS.RETRYING);
      const api = ownCaptchaRef.current;
      if (api && typeof api.reset === 'function') {
        api.reset();
      }
      return;
    }
    setCaptchaStatus(CAPTCHA_STATUS.ERROR);
    onError();
  }, [onError]);

  const handleLoaded = useCallback(() => {
    setCaptchaStatus(null);
    // Forward the widget API to the consumer's ref now that it is ready.
    if (captchaRef) {
      captchaRef.current = ownCaptchaRef.current;
    }
    onLoaded();
  }, [captchaRef, onLoaded]);

  const container = useTurnstileWidget({
    action,
    captchaRef: ownCaptchaRef,
    disabledCaptcha: config.turnstile.managed.disableValidation,
    locale,
    onBeforeInteractive: handleBeforeInteractive,
    onExpired: handleExpired,
    onError: handleError,
    onLoaded: handleLoaded,
    onResolved: handleResolved,
    siteKey: config.turnstile.managed.siteKey,
    size: effectiveSize,
    tabindex,
    theme: themeMode
  });

  // We only need it to trigger a re-render on resize so the render-phase
  // dimension read below picks up the new clientWidth.
  useLayoutEffect(() => {
    if (!referenceEl) {
      return undefined;
    }
    const onResize = () => setResizeTick(t => t + 1);
    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, [referenceEl]);

  if (
    config.turnstile.disableValidation ||
    config.turnstile.managed.disableValidation
  ) {
    return (
      <TurnstileSuccessMock
        style={props.style}
        heightScale={heightScale}
        size={size}
      />
    );
  }

  // Read clientWidth during render so it's always current (refs are attached
  // before render and re-reads happen on every re-render including resize ticks).
  // Subtract computed padding: clientWidth includes it but the widget must not.
  let availableWidth = baseWidth;
  const referenceElement = referenceEl && referenceEl.current;
  if (referenceElement) {
    const { paddingLeft, paddingRight } = getComputedStyle(referenceElement);
    availableWidth =
      referenceElement.clientWidth -
      parseFloat(paddingLeft || '0') -
      parseFloat(paddingRight || '0');
  }

  const scale = Math.min(1, availableWidth / baseWidth);
  const verticalScale = heightScale || 1;

  let ownStyle;
  if (effectiveSize === TURNSTILE_SIZE.FLEXIBLE) {
    ownStyle = {
      marginTop: 16,
      width: '100%',
      height: baseHeight
    };
  } else {
    ownStyle = {
      transform: `scale(${scale}, ${scale * verticalScale})`,
      transformOrigin: 'left top',
      marginTop: 16,
      width: baseWidth,
      height: baseHeight * scale * verticalScale
    };
  }

  let alertConfig = null;
  if (captchaStatus) {
    alertConfig = STATUS_CONFIG[captchaStatus] || null;
  }

  if (captchaStatus !== null) {
    fadeKeyRef.current = captchaStatus;
    lastAlertConfigRef.current = alertConfig;
  }

  let outerWrapperClass;
  if (effectiveSize === TURNSTILE_SIZE.COMPACT) {
    outerWrapperClass = classes.outerWrapperCompact;
  } else if (effectiveSize === TURNSTILE_SIZE.NORMAL) {
    outerWrapperClass = classes.outerWrapper;
  }

  return (
    <div className={outerWrapperClass}>
      <div ref={container} style={{ ...ownStyle, ...props.style }} />
      <div className={classes.alertWrapper}>
        <Collapse in={Boolean(alertConfig)} timeout={250}>
          <Fade
            in={Boolean(alertConfig)}
            key={fadeKeyRef.current}
            timeout={200}
          >
            <div>
              {lastAlertConfigRef.current && (
                <SweetAlert
                  type={lastAlertConfigRef.current.type}
                  message={lastAlertConfigRef.current.message}
                  iconSize={16}
                  classes={{
                    root: classes.compactAlert,
                    messageContainer: classes.compactAlertContent,
                    message: classes.compactAlertMessage
                  }}
                />
              )}
            </div>
          </Fade>
        </Collapse>
      </div>
    </div>
  );
};

// eslint-disable-next-line no-shadow
const useStyles = makeStyles(theme => ({
  outerWrapper: {
    maxWidth: TURNSTILE_WIDGET_DIMENSIONS_BY_SIZE[TURNSTILE_SIZE.NORMAL].width
  },
  outerWrapperCompact: {
    maxWidth: TURNSTILE_WIDGET_DIMENSIONS_BY_SIZE[TURNSTILE_SIZE.COMPACT].width
  },
  alertWrapper: {
    width: '100%',
    marginTop: theme.spacing(0.5)
  },
  compactAlert: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1)
  },
  compactAlertContent: {
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75)
  },
  compactAlertMessage: {
    fontSize: '0.7em'
  }
}));

TurnstileManagedCaptcha.propTypes = {
  locale: PropTypes.string,
  onExpired: PropTypes.func,
  onError: PropTypes.func,
  onResolved: PropTypes.func,
  onLoaded: PropTypes.func,
  style: PropTypes.object,
  tabindex: PropTypes.number,
  heightScale: PropTypes.number,
  theme: PropTypes.oneOf(['auto', 'light', 'dark']),
  size: PropTypes.oneOf(Object.values(TURNSTILE_SIZE)),
  visible: PropTypes.bool,
  action: PropTypes.oneOf(Object.values(RecaptchaAction))
};

TurnstileManagedCaptcha.defaultProps = {
  locale: '',
  onExpired: () => {},
  onError: () => {},
  onLoaded: () => {},
  onResolved: () => {},
  tabindex: 0,
  heightScale: 1,
  theme: 'light',
  size: TURNSTILE_SIZE.NORMAL,
  visible: true
};

export default TurnstileManagedCaptcha;
